// src/App.js

import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchCurrentUser } from "./userSlice";
import Navbar from "./components/Navbar";
import LoadingSpinner from "./components/LoadingSpinner";
import FloatingChatWidget from "./components/FloatingChatWidget";
import { TimezoneProvider } from "./contexts/TimezoneContext";
import { Toaster } from 'react-hot-toast';
import { isFeatureEnabled } from "./config/features";
// pages:
import RoomListPage from "./pages/RoomListPage";
import MeetingRoomPage from "./pages/MeetingRoomPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import RoomCreatePage from "./pages/RoomCreatePage";
import ManageRoomsPage from "./pages/ManageRoomsPage";
import OrganizationSettingsPage from "./pages/OrganizationSettingsPage";
import MyBookingsPage from "./pages/MyBookingsPage";
import OrganizationMembersPage from "./pages/OrganizationMembersPage";
import InviteRedirect from "./pages/InviteRedirect";
import AllBookingsPage from "./pages/AllBookingsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import OrganizationChoicePage from "./pages/OrganizationChoicePage";
import JoinOrganizationPage from "./pages/JoinOrganizationPage";
import JoinRequestPendingPage from "./pages/JoinRequestPendingPage";
import UserProfilePage from "./pages/UserProfilePage";

// Onboarding components
import OnboardingWelcomeStep from "./components/OnboardingWelcomeStep";
import OnboardingBillingStep from "./components/OnboardingBillingStep";
import OnboardingCompletionStep from "./components/OnboardingCompletionStep";

// existing routes:
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import GuestRoute from "./components/GuestRoute";
import OnboardingRoute from "./components/OnboardingRoute";
import OrganizationChoiceRoute from "./components/OrganizationChoiceRoute";

// RootRedirect is used for "/"
function RootRedirect() {
  const { user, status } = useSelector((state) => state.user);
  
  // Show loading state while checking authentication
  if (status === "loading" || user === undefined) {
    return <LoadingSpinner message="Checking authentication..." />;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // Check if user has no organization - redirect to organization choice
  if (!user.organization) {
    return <Navigate to="/organization-choice" />;
  }
  
  // Check if user needs onboarding (new organization admin)
  // If they're joining via invitation, they go straight to /rooms
  // If they created their own organization, they need onboarding
  if (user.organization && user.organization.onboarding && !user.organization.onboarding.isCompleted) {
    const currentStep = user.organization.onboarding.currentStep || 'welcome';
    return <Navigate to={`/onboarding/${currentStep}`} />;
  }
  
  return <Navigate to="/rooms" />;
}

// NotFoundRedirect handles unknown paths
function NotFoundRedirect() {
  const { user, status } = useSelector((state) => state.user);
  
  // While loading, don't redirect anywhere
  if (status === "loading" || user === undefined) {
    return <LoadingSpinner message="Loading..." />;
  }
  
  // Redirect authenticated users to /rooms, unauthenticated to /login
  return user ? <Navigate to="/rooms" replace /> : <Navigate to="/login" replace />;
}

function App() {
  const dispatch = useDispatch();
  const { status } = useSelector((state) => state.user);

  // CRITICAL: Check authentication status on app initialization
  // This prevents redirect to /rooms on page refresh when user should stay on current page
  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch, status]);

  return (
    <TimezoneProvider>
      <Router>
        <Navbar />
        
        {/* Toast notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              style: {
                background: '#4caf50',
                color: '#fff',
              },
            },
            error: {
              duration: 5000,
              style: {
                background: '#f44336',
                color: '#fff',
              },
            },
          }}
        />
        
        {/* Floating Chat Widget - appears on all pages */}
        {isFeatureEnabled('AI_CHAT_WIDGET') && <FloatingChatWidget />}
        
        <Routes>
        {/* Root path */}
        <Route path="/" element={<RootRedirect />} />

        {/* GUEST ROUTES */}
        <Route path="/login" element={<GuestRoute element={<LoginPage />} />} />
        <Route
          path="/register"
          element={<GuestRoute element={<RegisterPage />} />}
        />
        <Route
          path="/forgot-password"
          element={<GuestRoute element={<ForgotPasswordPage />} />}
        />
        <Route
          path="/reset-password"
          element={<GuestRoute element={<ResetPasswordPage />} />}
        />
        <Route
          path="/organization-choice"
          element={<OrganizationChoiceRoute element={<OrganizationChoicePage />} />}
        />

        {/* ONBOARDING ROUTES */}
        <Route
          path="/onboarding/welcome"
          element={<OnboardingRoute element={<OnboardingWelcomeStep />} step="welcome" />}
        />
        <Route
          path="/onboarding/billing"
          element={<OnboardingRoute element={<OnboardingBillingStep />} step="billing" />}
        />
        <Route
          path="/onboarding/complete"
          element={<OnboardingRoute element={<OnboardingCompletionStep />} step="complete" />}
        />

        {/* PROTECTED ROUTES */}
        <Route
          path="/rooms"
          element={<ProtectedRoute element={<RoomListPage />} />}
        />
        <Route
          path="/book-room/:roomId"
          element={<ProtectedRoute element={<MeetingRoomPage />} />}
        />
        <Route
          path="/my-bookings"
          element={<ProtectedRoute element={<MyBookingsPage />} />}
        />

        {/* ADMIN ROUTES */}
        <Route
          path="/create-room"
          element={<AdminRoute element={<RoomCreatePage />} />}
        />
        <Route
          path="/manage-rooms"
          element={<AdminRoute element={<ManageRoomsPage />} />}
        />
        <Route
          path="/org-settings"
          element={<AdminRoute element={<OrganizationSettingsPage />} />}
        />
        <Route
          path="/org-members"
          element={<AdminRoute element={<OrganizationMembersPage />} />}
        />
        <Route
          path="/all-bookings"
          element={<AdminRoute element={<AllBookingsPage />} />}
        />

        {/* invitation route */}
        <Route path="/invite/:token" element={<InviteRedirect />} />

        {/* organization join route */}
        <Route path="/join-org/:orgId" element={<JoinOrganizationPage />} />

        {/* join request pending page */}
        <Route path="/join-pending" element={<JoinRequestPendingPage />} />

        {/* user profile page */}
        <Route path="/user-profile" element={<ProtectedRoute element={<UserProfilePage />} />} />

        {/* fallback for unknown paths */}
        <Route path="*" element={<NotFoundRedirect />} />
      </Routes>
    </Router>
    </TimezoneProvider>
  );
}

export default App;
